# Route: /admin/tenants/create
# Create a new tenant — wizard-style form with live review section.
# Permission: SystemAdmin (gated by ISystemAdminGate at the API; tenant-scoped
# Admin users are rejected at /api/platform/tenants with 403).
#
# F73c (ADR-168) — refactored from a Section + Button + action.type=custom
# pattern (which emitted a fetch that read form state from a context never
# populated by the input children → silent 400 on submit) to the canonical
# type: Form + type: FormActions pattern used by every other create page
# (tracking form-create / entry-create). FormEmitter emits `_onSubmit`
# that captures values via native `FormData(form)`, dispatching the
# xp85-action event with the captured payload — bypasses the broken
# context-propagation path entirely.

page-admin-tenant-create:
  type: Page
  archetype: create
  meta:
    title: $t.admin.tenants.create_title
    auth: required
  data:
    form:
      model: CreateTenantForm
      initial:
        name: ""
        tenantId: ""
        adminEmail: ""
        adminFirstName: ""
        adminLastName: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - tenant-create-header
    - tenant-create-form

# ── Header ───────────────────────────────────────────────────────────────────

tenant-create-header:
  type: PageHeader
  title: $t.admin.tenants.create_title
  back_route: /admin/tenants

# ── Form ─────────────────────────────────────────────────────────────────────
#
# type: Form emits a native <form> element. Input children bind
# `form.<field>` — the page's `form` seed, which the review section reads too —
# and render as <input name="<field>">; the Form's `_collectFormData` submits
# each under the CreateTenantForm field its path names and dispatches the
# canonical xp85-action event. The
# data-rest provider's TenantResponse `create` route (/api/platform/tenants)
# completes the POST.

tenant-create-form:
  type: Form
  model: CreateTenantForm
  a11y:
    label: $t.admin.tenants.create_title
  action:
    type: create
    model: TenantResponse
    on_success: navigate(/admin/tenants)
  spatial:
    display: flex
    direction: column
    gap: $spacing.md
  children:
    - tenant-form-card
    - tenant-form-review
    - tenant-form-actions

tenant-form-card:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.lg
    padding: $spacing.lg
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - tenant-form-name
    - tenant-form-id
    - tenant-form-email
    - tenant-form-admin-first-name
    - tenant-form-admin-last-name

tenant-form-name:
  type: Input
  input_type: text
  label: $t.admin.tenants.field_name
  bind:
    content: form.name
  a11y:
    label: $t.admin.tenants.field_name_label
  validation:
    required: true
    messages:
      invalid: $t.admin.tenants.field_name_invalid
  spatial:
    min_height: $size.touch-target

tenant-form-id:
  type: Input
  input_type: text
  label: $t.admin.tenants.field_id
  bind:
    content: form.tenantId
  a11y:
    label: $t.admin.tenants.field_id_label
  # Mirrors the server's one shape rule — the Tenant aggregate's, enforced by
  # Tenant.Provision. Start with a letter, end with a letter or digit, 3..50 chars.
  # The previous `^[a-z0-9-]+$` accepted `9acme` and a 100-character id, both refused
  # after a round-trip — the error an operator should never have to discover.
  # A reserved id (`admin`, `smoke`, …) is refused by the server and deliberately NOT
  # mirrored here: the list is policy that changes without the browser being redeployed.
  # The hyphen inside [...] is escaped: the browser compiles `pattern` with the `v` flag
  # and ignores a pattern that fails there (XP85043 refuses one at lint).
  validation:
    required: true
    pattern: '^[a-z][a-z0-9\-]{1,48}[a-z0-9]$'
    messages:
      required: $t.admin.tenants.tenant_id_required
      pattern: $t.admin.tenants.tenant_id_invalid
  spatial:
    min_height: $size.touch-target

tenant-form-email:
  type: Input
  input_type: email
  label: $t.admin.tenants.field_admin_email
  bind:
    content: form.adminEmail
  a11y:
    label: $t.admin.tenants.field_admin_email_label
  validation:
    required: true
  spatial:
    min_height: $size.touch-target

# The initial administrator is a named person: the directory user the tenant is
# provisioned with carries a first and a last name, 1..255 characters each, and
# the server refuses a create without them.
tenant-form-admin-first-name:
  type: Input
  input_type: text
  label: $t.admin.tenants.field_admin_first_name
  helper: $t.admin.tenants.admin_name_hint
  bind:
    content: form.adminFirstName
  a11y:
    label: $t.admin.tenants.field_admin_first_name_label
  # Mirrors PersonName (Xp87.Domain): at least one letter, and none of the characters
  # Keycloak's person-name rule prohibits — < > & " $ % ! # ? § ; * ~ / \ | ^ = [ ] { } ( ) —
  # nor a control character, a line or paragraph separator, or a text-direction control
  # (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069; the two hyphens in the
  # class are ranges). The zero-width joiner and non-joiner stay allowed. Every punctuation
  # mark is escaped, and the four HTML-significant ones are written as \xNN: the browser
  # compiles `pattern` with the `v` flag and ignores one that fails there (XP85043). The
  # 255-character bound is the bound model field's `max_length`, which emits `maxlength`.
  validation:
    required: true
    pattern: '^(?=.*\p{L})[^\x22\x26\x3C\x3E\$\%\!\#\?§\;\*\~\/\\\|\^\=\[\]\{\}\(\)\p{Cc}\u2028\u2029\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+$'
    messages:
      required: $t.admin.tenants.admin_first_name_required
      pattern: $t.admin.tenants.admin_name_invalid
  spatial:
    min_height: $size.touch-target

tenant-form-admin-last-name:
  type: Input
  input_type: text
  label: $t.admin.tenants.field_admin_last_name
  bind:
    content: form.adminLastName
  a11y:
    label: $t.admin.tenants.field_admin_last_name_label
  # Mirrors PersonName (Xp87.Domain): at least one letter, and none of the characters
  # Keycloak's person-name rule prohibits — < > & " $ % ! # ? § ; * ~ / \ | ^ = [ ] { } ( ) —
  # nor a control character, a line or paragraph separator, or a text-direction control
  # (U+061C, U+200E, U+200F, U+202A to U+202E, U+2066 to U+2069; the two hyphens in the
  # class are ranges). The zero-width joiner and non-joiner stay allowed. Every punctuation
  # mark is escaped, and the four HTML-significant ones are written as \xNN: the browser
  # compiles `pattern` with the `v` flag and ignores one that fails there (XP85043). The
  # 255-character bound is the bound model field's `max_length`, which emits `maxlength`.
  validation:
    required: true
    pattern: '^(?=.*\p{L})[^\x22\x26\x3C\x3E\$\%\!\#\?§\;\*\~\/\\\|\^\=\[\]\{\}\(\)\p{Cc}\u2028\u2029\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]+$'
    messages:
      required: $t.admin.tenants.admin_last_name_required
      pattern: $t.admin.tenants.admin_name_invalid
  spatial:
    min_height: $size.touch-target

# ── Live review section (shows what's about to be POSTed) ────────────────────

tenant-form-review:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.sm
    padding: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-variant
    radius: $radius.md
  children:
    - tenant-review-title
    - tenant-review-name
    - tenant-review-id
    - tenant-review-email
    - tenant-review-admin-first-name
    - tenant-review-admin-last-name

tenant-review-title:
  type: Text
  content: $t.admin.tenants.review_title
  a11y:
    role: heading
    level: 2
  tokens:
    font_size: $font.size.md
    color: $color_scheme.light.color.on-surface

tenant-review-name:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-review-name-label
    - tenant-review-name-value

tenant-review-name-label:
  type: Text
  content: $t.admin.tenants.field_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-review-name-value:
  type: Text
  bind:
    content: form.name
  tokens:
    font_size: $font.size.sm

tenant-review-id:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-review-id-label
    - tenant-review-id-value

tenant-review-id-label:
  type: Text
  content: $t.admin.tenants.field_id
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-review-id-value:
  type: Text
  bind:
    content: form.tenantId
  tokens:
    font_size: $font.size.sm

tenant-review-email:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-review-email-label
    - tenant-review-email-value

tenant-review-email-label:
  type: Text
  content: $t.admin.tenants.field_admin_email
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-review-email-value:
  type: Text
  bind:
    content: form.adminEmail
  tokens:
    font_size: $font.size.sm

tenant-review-admin-first-name:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-review-admin-first-name-label
    - tenant-review-admin-first-name-value

tenant-review-admin-first-name-label:
  type: Text
  content: $t.admin.tenants.field_admin_first_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-review-admin-first-name-value:
  type: Text
  bind:
    content: form.adminFirstName
  tokens:
    font_size: $font.size.sm

tenant-review-admin-last-name:
  type: Section
  spatial:
    display: flex
    gap: $spacing.sm
  children:
    - tenant-review-admin-last-name-label
    - tenant-review-admin-last-name-value

tenant-review-admin-last-name-label:
  type: Text
  content: $t.admin.tenants.field_admin_last_name
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted

tenant-review-admin-last-name-value:
  type: Text
  bind:
    content: form.adminLastName
  tokens:
    font_size: $font.size.sm

# ── Submit / Cancel actions ──────────────────────────────────────────────────

tenant-form-actions:
  type: FormActions
  submit_label: $t.admin.tenants.create_submit
  cancel_route: /admin/tenants
